import {
    Col,
    Row,
    Typography,
    Form,
    Input,
    Button,
    message,
    Modal,
    Checkbox,
    Select,
    DatePicker,
    Switch,
} from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useEffect, useState } from "react";
import { regionTimezoneOffsetMap} from "../../regionData";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useOrganization } from "../organization/OrganizationContext";
import {createMatchActivity} from "../../apiBridge";
import dayjs from 'dayjs';
import { UserInfo } from "../../interface";
import ActivityTimeInput from "./_activityTimeInput";
import ActivityLogoUpload from "./_activityLogoUpload";

const { Title } = Typography;

const CreateActivity: React.FC = () => {
    const user = useOutletContext() as UserInfo; 

    const { organization, subscriptionFeatures, loading } = useOrganization();
    const tournamentExpiredAt = null;
    
    const [form] = Form.useForm();
    const navigate = useNavigate()
    const [messageApi, contextHolder] = message.useMessage();

    const [loadingState, setLoadingState] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);

    const startDate = Form.useWatch("start_date", form);
    const endDate = Form.useWatch("end_date", form);
    const matchTimes = Form.useWatch("matchTimes", {form, preserve: true}) as { date: string, start_time: string }[] | undefined;

    const disabledEndDate = (current: dayjs.Dayjs) => {
        if (!tournamentExpiredAt) return false;
        return current && current > dayjs(tournamentExpiredAt);
    };

    useEffect(() => {
        for (let i = 0; i < subscriptionFeatures.length; i++) {
            if (subscriptionFeatures[i].name === "tournament_mode") {
                setHasSubscription(true);
                break;
            }
        }
    }, [subscriptionFeatures, navigate, messageApi]);

    useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);

            const userTimezoneOffset = -start.getTimezoneOffset() * 60000;
            start.setTime(start.getTime() + userTimezoneOffset);
            end.setTime(end.getTime() + userTimezoneOffset);
            const dateList: { date: string, start_time: string }[] = [];

            for (let d = start; d <= end && dateList.length < 15; d.setDate(d.getDate() + 1)) {
                dateList.push({ date: d.toISOString().split('T')[0], start_time: '' });
            }

            form.setFieldsValue({ matchTimes: dateList });
        } else {
            form.setFieldsValue({ matchTimes: [] });
        }
    }, [startDate, endDate, form]);

    if (loading) {
        return (
            <div style={{ 
                background: "white", 
                borderRadius: 4, 
                boxShadow: "0 0 3px 0 #cbcbcb", 
                padding: 20,
                textAlign: "center"
            }}>
                <Typography.Title level={4}>Loading...</Typography.Title>
            </div>
        );
    }

    if (!hasSubscription || user.role !== "Owner") {
        return (
            <div style={{ 
                background: "white", 
                borderRadius: 4, 
                boxShadow: "0 0 3px 0 #cbcbcb", 
                padding: 20,
                textAlign: "center"
            }}>
                <Typography.Title level={4}>No Permission</Typography.Title>
                <Typography.Paragraph>
                    Your current subscription plan does not include permission to create. Please upgrade your subscription plan to use this feature.
                </Typography.Paragraph>
                <Button type="primary" onClick={() => navigate("/activity")}>
                    Back to List
                </Button>
            </div>
        );
    }

    const onFinishCreate = async (values: any) => {
        const organizationId = organization?.ID;
        if (!organizationId) {
            messageApi.error("Organization is unavailable. Please refresh and try again.");
            return;
        }

        Modal.confirm({
            title: 'Confirm Tournament Creation',
            content: 'Once confirmed, this cannot be changed. You will need to recreate a new tournament if any of this are not correct.',
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: async () => {
                const payload = {
                    organization_id: organizationId,
                    name: values.name,
                    description: values.description ? values.description : '',
                    start_at: dayjs(values.start_date).format('YYYY-MM-DD'),
                    end_at: dayjs(values.end_date).format('YYYY-MM-DD'),
                    match_type: values.match_type || 'SingleElimination',
                    allow_register: values.allow_register,
                    max_court_count: values.max_court_count,
                    display_policy: values.public ? 'public' : 'private',
                    match_tz: 0,
                    options: {
                        location: values.location,
                        display_name: values.display_name,
                        logo: values.logo || "",
                        start_time: values.matchTimes,
                    }
                }

                setLoadingState(true)
                const res = await createMatchActivity(payload)
                if (res) {
                    const data = await res.json();
                    if (data.id) {
                        messageApi.success("Activity created successfully")
                        form.resetFields();

                        navigate(`/activity/${data.id}/info`)
                    } else {
                        setLoadingState(false);

                        if (data?.error) {
                            messageApi.error(data.error);
                            return false;
                        } else {
                            messageApi.error('Failed to create activity');
                            return false;
                        }
                    }
                }
            }
        });
    };

    const handleApplyAllDaysChange = (e: CheckboxChangeEvent) => {
        //setApplyAllDays(e.target.checked);

        if (e.target.checked && matchTimes && matchTimes.length > 0) {
            const firstStartTime = form.getFieldValue(['matchTimes', 0, 'start_time']);
            if (firstStartTime) {
                const updatedMatchTimeList = matchTimes.map((matchTime, index) => ({
                    ...matchTime,
                    start_time: firstStartTime
                }));
                form.setFieldsValue({ matchTimes: updatedMatchTimeList });
            }
        }
    };

    const initialValues = () => {
        return {
            organization_id: organization?.ID,
            public: false,
            registration_open: false,
            max_court_count: 1,
            match_type: 'SingleElimination',
        };
    };


    return (
        <>
            {contextHolder}

            <Form
                form={form}
                name="createActivityForm"
                layout="vertical"
                onFinish={onFinishCreate}
                initialValues={initialValues()}
            >
                <div style={{ background: "white", borderRadius: 4, boxShadow: "0 0 3px 0 #cbcbcb", padding: 20 }}>
                    <Title level={4}>Create Tournament </Title>

                    <Row gutter={24}>
                        <Col sm={12} xs={24}>
                            <Form.Item label="Tournament Name" rules={[{ required: true }]} required={true} name="name">
                                <Input />
                            </Form.Item>
                        </Col>

                        
                        <Col sm={6} xs={24}>
                            <Form.Item label="Display Name" name="display_name">
                                 <Input />
                            </Form.Item>
                        </Col>

                        <Col sm={6} xs={24}>
                            <Form.Item label="Organization" name="organization_id">
                                <Select disabled>
                                    <Select.Option value={organization?.ID}>
                                        {organization?.Name || "No Organization"}
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col sm={12} xs={24}>
                            <Form.Item
                                label="Bracket Structure"
                                name="match_type"
                                rules={[{ required: true }]}
                                extra="Default bracket type for this tournament's categories."
                            >
                                <Select
                                    options={[
                                        { value: 'SingleElimination', label: 'Single Elimination (with Bronze Match)' },
                                        { value: 'SingleEliminationNoThirdPlace', label: 'Single Elimination (No Bronze Match)' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>


                        <Col sm={12} xs={24}>
                            <Form.Item label="Tournament Description" name="description">
                                <Input.TextArea />
                            </Form.Item>
                        </Col>


                        <Col sm={12} xs={24}>
                            <Row gutter={24}>
                                <Col xs={16}>
                                    <Form.Item label="Tournament Location" name="location" rules={[{ required: true }]}>
                                        <Select 
                                            showSearch
                                            placeholder="Search and select location"
                                            filterOption={(input, option) =>
                                                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {Object.keys(regionTimezoneOffsetMap).map((region) => (
                                                <Select.Option key={region} value={region}>
                                                    {region}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>

                        <Col sm={12} xs={24}>
                            <Row gutter={24}>
                                <Col xs={12}>
                                    <Form.Item label="Start Date" name="start_date" rules={[{ required: true }]} required={true}>
                                        <DatePicker style={{ width: "100%" }} />
                                    </Form.Item>
                                </Col>

                                <Col xs={12}>
                                    <Form.Item label="End Date" name="end_date" rules={[{ required: true }]} required={true}>
                                        <DatePicker 
                                            style={{ width: "100%" }} 
                                            disabledDate={disabledEndDate}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>

                        <Col sm={12} xs={24} style={{ display: "none" }}>
                            <Row gutter={24}>
                                <Col xs={12}>
                                    <Form.Item label="Registration Status" name="allow_register">
                                        <Switch />
                                    </Form.Item>
                                </Col>

                                <Col xs={12}>
                                    <Form.Item label="Public" name="public">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>

                        <Col sm={12} xs={24}>
                            <Row gutter={24}>
                                <Col xs={12}> 
                                    <Form.Item 
                                        label="Max Court" 
                                        name="max_court_count" 
                                        initialValue={2}
                                        normalize={(value) => Number(value)}
                                        rules={[
                                            { required: true, message: 'Please input max court' },
                                            { type: 'number', min: 1, max: 2, message: 'Max court must be between 1 and 2' }
                                        ]}
                                    >
                                        <Input type="number" min={1} max={2} />
                                    </Form.Item>
                                </Col>

                                <Col xs={12}>
                                    <Form.Item label="Tournament Logo" name="logo">
                                        <ActivityLogoUpload />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>

                <div style={{ background: "white", borderRadius: 4, boxShadow: "0 0 3px 0 #cbcbcb", marginTop: 20, padding: 20 }}>
                    {!matchTimes || matchTimes.length === 0 ? (
                        <div>Please set Start Date and End Date</div>
                    ) : (
                        <>
                            <Title level={5}>Start Time</Title>
                            <Form.List name="matchTimes">
                                {(fields) => (
                                    <Row gutter={24}>
                                        {fields.map((field) => (
                                            <Col key={field.key} sm={4}>
                                                <Form.Item
                                                    {...field}
                                                    label={matchTimes[field.name].date}
                                                    name={[field.name, 'start_time']}
                                                    fieldKey={[field.name, 'start_time'].join('.')}
                                                    rules={[{ required: true, message: 'Please input start time!' }]}
                                                >
                                                    <ActivityTimeInput />
                                                </Form.Item>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </Form.List>


                            <Row gutter={24} style={{ marginBottom: 16 }}>
                                <Col xs={24}>
                                    <Checkbox onChange={handleApplyAllDaysChange}>Apply All Days</Checkbox>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>

                <Form.Item style={{ marginTop: 20 }}>
                    <Button type="primary" htmlType="submit" loading={loadingState}>
                        Create
                    </Button>
                </Form.Item>
            </Form>
        </>
    );
};

export default CreateActivity;
